"use client";
import {
    IAccountAssets,
    IBlacklistUrls,
    IBlog,
    IBlogPost,
    IConnections,
    IPages,
    IUserData,
    IUsersBrandAccounts,
} from "@/types";
import { HeadingDefault } from "./heading/Heading";
import { useEffect, useRef, useState } from "react";
import React from "react";
import {
    addImportantToStyles,
    cn,
    getTextColorForBackground,
    rgbToHex,
} from "@/lib/utils";
import Blocks from "./blocks";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";

import { IconHelpCircle, IconShare, IconX } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import AddBlock, { AddBlockContent } from "../content/AddBlock";
import Theme from "../content/Theme";
import PagesDir from "../content/PagesDir";
import { useWebSocketImpersonate } from "@/lib/ws";
import Collaborative from "./collaborative";
import BottomNav from "./BottomNav";
import BlogPost from "../blog/post";
import { BlogPostSkeletonBlock } from "../blog/skeleton";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import SidebarDrawer from "../layout/SidebarDrawer";
import ProfileShare from "./ProfileShare";
import { useIsMobile } from "@/hooks/use-mobile";
import { Separator } from "@/components/ui/separator";
import TourButton from "@/components/admin-layout-ui/onboarding/TourButton";
import { HelpCircle } from "lucide-react";
import NewUIToggle from "@/components/render/new-ui-toggle";
interface Props {
    user: IUserData;
    admin: boolean;
    className?: string;
    assets: IAccountAssets[];
    page?: IPages;
    pages?: IPages[];
    connections: {
        id: string;
        provider:
        | "twitch"
        | "youtube"
        | "twitter"
        | "bluesky"
        | "threads"
        | "instagram"
        | "tiktok"
        | "trovo";
        data: {
            [key: string]: any;
        };
    }[];
    impersonate?: boolean;
    loggedInUser?: IUsersBrandAccounts | undefined;
    domain?: boolean;
    posts?: IBlogPost[];
    post?: IBlogPost | null;
    blog?: IBlog | null;
    blackListUrls?: IBlacklistUrls[] | null;
}

interface ExpandBlockEvent extends CustomEvent {
    detail: {
        id?: string;
        content: React.ReactNode;
        title?: string;
        description?: string;
        titleOptions?: React.ReactNode;
    };
}
export default function Render({
    user,
    admin,
    className,
    assets,
    page,
    pages,
    connections,
    impersonate,
    loggedInUser,
    domain,
    posts,
    post,
    blog,
    blackListUrls,
}: Props) {
    const pathname = usePathname();
    const { ws } = useWebSocketImpersonate({ impersonate, user, page });
    const [collaborativeAccounts, setCollaborativeAccounts] = React.useState(
        []
    );
    const [theme, setTheme] = React.useState(user.theme);
    const [blogTheme, setBlogTheme] = React.useState(blog?.theme);
    React.useEffect(() => {
        setBlogTheme(blog?.theme);
    }, [blog?.theme]);
    const [isEditingBlogStyles, setIsEditingBlogStyles] = useState(false);
    const [newPages, setNewPages] = useState(
        pages?.sort((a, b) => a.sort - b.sort) || []
    );
    React.useEffect(() => {
        if (impersonate && ws) {
            const handleMessage = (event: MessageEvent) => {
                try {
                    const data = JSON.parse(event.data);
                    switch (data.type) {
                        case "update_theme":
                            setTheme(data.theme);
                            break;
                        case "update_blog_theme":
                            setBlogTheme(data.theme);
                            break;
                        case "update_blocks":
                            window.dispatchEvent(
                                new CustomEvent("ws_update_blocks", {
                                    detail: {
                                        data: data.blocks,
                                    },
                                })
                            );
                            break;
                        case "update_header":
                            window.dispatchEvent(
                                new CustomEvent("ws_update_header", {
                                    detail: {
                                        data: data.data,
                                    },
                                })
                            );
                            break;
                        case "stats":
                            setCollaborativeAccounts(
                                data.accounts.filter(
                                    (e: any) => e != loggedInUser?.user_id
                                )
                            );
                            break;
                        default:
                            break;
                    }
                    // Handle message specific to Component B
                } catch (error) {
                    console.error(
                        "Render- Error parsing WebSocket message:",
                        error
                    );
                }
            };
            ws.addEventListener("message", handleMessage);
            return () => {
                ws.removeEventListener("message", handleMessage);
            };
        }
    }, [impersonate, ws, loggedInUser?.user_id]);
    // motion
    const ref = useRef(null);
    // const isInView = useInView(ref);

    // expand
    const handleClickOutside = (event: React.MouseEvent<HTMLDivElement>) => {
        if (isExpanded) {
            setIsExpanded(false);
            setExpandedContent(null);
        }
    };
    const [isExpanded, setIsExpanded] = useState(false);

    const [expandedContent, setExpandedContent] = useState<{
        id: string;
        title?: string;
        description?: string;
        content: React.ReactNode;
        titleOptions?: React.ReactNode;
    } | null>({
        id: "",
        content: "",
    });
    const isMobile = useIsMobile();
    useEffect(() => {
        const handleExpandEvent = (event: ExpandBlockEvent) => {
            setIsExpanded(true);
            if (event.detail.content) {
                setExpandedContent({
                    id: event.detail?.id || "",
                    title: event.detail.title,
                    description: event.detail.description,
                    content: event.detail.content,
                    titleOptions: event.detail.titleOptions,
                });
            } else {
                setExpandedContent({
                    id: "-1" + crypto.randomUUID(),
                    title: "Add",
                    description:
                        "Drag and drop blocks to add them to your profile",
                    content: (
                        <AddBlockContent
                            random={Math.random()}
                            account={user}
                            isMobile={isMobile}
                        />
                    ),
                });
            }
        };

        const handleCollapseEvent = () => {
            setIsExpanded(false);
            setExpandedContent({
                id: "-1" + crypto.randomUUID(),
                content: "",
            });
        };

        window.addEventListener(
            "expandBlock",
            handleExpandEvent as EventListener
        );
        window.addEventListener("collapseBlock", handleCollapseEvent);

        return () => {
            window.removeEventListener(
                "expandBlock",
                handleExpandEvent as EventListener
            );
            window.removeEventListener("collapseBlock", handleCollapseEvent);
        };
    }, [isMobile, user]);
    // Blog Styles
    const [showLatestPost, setShowLatestPost] = useState(false);
    const [latestPost, setLatestPost] = useState<IBlogPost | null>(
        posts
            ? {
                ...posts[0],
                author: loggedInUser ? loggedInUser.user : user,
            }
            : null
    );
    // user things
    if (!user.settings?.pronouns) {
        user.settings.pronouns = "";
    }
    if (!user.settings?.location) {
        user.settings.location = "";
    }
    const [editMode, setEditMode] = useState(admin);
    let font_style = {};
    if (!theme.font || theme.font.font.length == 0) {
        font_style = {};
    } else {
        font_style = {
            fontFamily: theme.font?.font,
        };
    }
    let BackgroundStyle: any = {};
    if (theme?.background_img && theme?.background_img.length != 0) {
        BackgroundStyle.backgroundImage = `url(${theme.background_img})`;
        BackgroundStyle.backgroundSize = "cover";
        BackgroundStyle.backgroundPosition = "center";
        // BackgroundStyle.backgroundAttachment = "fixed";
    } else {
        BackgroundStyle.background = theme.background;
        // BackgroundStyle.backgroundAttachment = "fixed"; // Add this line
    }
    const title =
        user.settings.branding == true
            ? `${user.displayname}`
            : `${user.displayname} | Doras.to`;
    const postTitle =
        user.settings.branding == true
            ? `${post?.title} - ${user.displayname}`
            : `${post?.title} - ${user.displayname} | Doras.to`;

    // const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const handleToggleBlogStyles = async (event: Event) => {
            const customEvent = event as CustomEvent<{ isEditing: boolean }>;
            setShowLatestPost(customEvent.detail.isEditing);
            if (customEvent.detail.isEditing) {
                setIsLoading(true);
                setIsLoading(false);
            }
        };

        window.addEventListener("toggleBlogStyles", handleToggleBlogStyles);

        return () => {
            window.removeEventListener(
                "toggleBlogStyles",
                handleToggleBlogStyles
            );
        };
    }, [user.id]);

    const textColor = theme.accent_col
        ? rgbToHex(theme.accent_col)
        : theme.text_col_button
            ? theme.text_col_button
            : theme.button;
    const backgroundColor = getTextColorForBackground(
        theme.accent_col
            ? rgbToHex(theme.accent_col)
            : theme.text_col_button
                ? theme.text_col_button
                : theme.button
    );
    return (
        <div className="relative flex h-full flex-col">
            <SidebarProvider
                className="max-h-dvh !overflow-hidden"
                name="content-editor"
                defaultOpen={false}
                open={isMobile == false && isExpanded}
                onOpenChange={(value) => {
                    if (!isMobile) {
                        [
                            setIsExpanded(value),
                            window.dispatchEvent(
                                new CustomEvent("closeBlock", {
                                    detail: {
                                        id: "-1",
                                    },
                                })
                            ),
                        ];
                    }
                }}
                style={{
                    // @ts-ignore
                    "--sidebar-width": "38rem",
                }}
            >
                <SidebarInset className="relative overflow-auto">
                    {((theme?.css &&
                        theme.css.length !== 0 &&
                        theme.css !== undefined &&
                        user.account_type === "premium") ||
                        (theme?.css &&
                            theme.css.length !== 0 &&
                            theme.css !== undefined &&
                            user.account_type === "bplus") ||
                        (theme?.css &&
                            theme.css.length !== 0 &&
                            theme.css !== undefined &&
                            user.account_type === "bpro")) && (
                            <>
                                <style
                                    dangerouslySetInnerHTML={{
                                        __html: `${theme.css.match(/@keyframes\s+[^{]+\s*{(?:[^{}]*{[^{}]*})*[^{}]*}/g)?.join("") || ""}`,
                                    }}
                                />
                                <style
                                    dangerouslySetInnerHTML={{
                                        __html: `#user-root { ${addImportantToStyles(theme.css.replace(/@keyframes\s+[^{]+\s*{(?:[^{}]*{[^{}]*})*[^{}]*}/g, ""))} }`,
                                    }}
                                />
                            </>
                        )}
                    <style>
                        {`:root { 
                --primary-color: ${theme.button}; 
                --primary-text-color: ${theme.text_col_button};
                --secondary-color: ${theme.accent_col};
                --background-color: ${theme.background};
                --blog-background-color: ${blogTheme?.background};
                --blog-button-color: ${blogTheme?.button_bg};
                --blog-button-text-color: ${blogTheme?.button_text};
                --blog-heading-text: ${blogTheme?.heading_text};
                }}`}
                    </style>
                    {admin && (
                        <div className="flex items-center justify-between rounded-b-md border-b bg-sidebar p-1">
                            <div className="flex w-full items-center justify-start">
                                <div
                                    className="flex w-fit shrink-0 items-center gap-2"
                                    id="onboarding-top-nav"
                                >
                                    <AddBlock
                                        brand={impersonate}
                                        account={user}
                                    />
                                    <Theme
                                        ws={ws}
                                        user={user}
                                        theme={theme}
                                        setTheme={setTheme}
                                        assets={assets}
                                        impersonate={impersonate}
                                        loggedInUser={loggedInUser}
                                        blogTheme={blogTheme}
                                        setBlogTheme={setBlogTheme}
                                        setIsEditingBlogStyles={
                                            setIsEditingBlogStyles
                                        }
                                        isEditingBlogStyles={
                                            isEditingBlogStyles
                                        }
                                    />
                                    <div className="flex h-auto items-center gap-1 rounded-md rounded-md bg-muted text-sm">
                                        <Button
                                            className={`group relative hidden h-auto w-fit shrink-0 justify-start gap-2 rounded-md bg-muted text-sm text-foreground transition-all duration-300 hover:bg-primary hover:text-primary-foreground disabled:text-muted-foreground disabled:opacity-100 data-[state=open]:bg-primary data-[state=open]:text-primary-foreground md:flex`}
                                            disabled
                                        >
                                            {pathname == "/admin/content"
                                                ? "Home"
                                                : "/" + page?.slug}
                                        </Button>
                                        <PagesDir
                                            user={user}
                                            pages={newPages || []}
                                            impersonate={impersonate}
                                            setNewPages={setNewPages}
                                        />
                                    </div>
                                    <NewUIToggle account={user} />
                                </div>
                                <div className="flex w-full items-center justify-end gap-2">
                                    {impersonate && (
                                        <div className="flex flex-col">
                                            <div className="flex -space-x-3">
                                                <Collaborative
                                                    collaborativeAccounts={
                                                        collaborativeAccounts
                                                    }
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex w-fit shrink-0 gap-2">
                                        {/* <Share
                                            user={user}
                                            className="bg-primary text-primary-foreground hover:bg-primary/70"
                                        /> */}
                                        <ProfileShare
                                            user={user}
                                            theme={theme}
                                            admin={true}
                                            customTrigger={
                                                <Button
                                                    // variant="navigationAccent"
                                                    className={`group relative flex h-auto shrink-0 gap-2 rounded-md bg-primary text-sm text-foreground transition-all duration-300 hover:bg-muted hover:text-primary-foreground`}
                                                >
                                                    <IconShare size={16} />
                                                    <Label
                                                        variant="toggle"
                                                        className="hidden cursor-pointer pb-0 text-sm md:flex"
                                                    >
                                                        Share
                                                    </Label>
                                                </Button>
                                            }
                                        />
                                    </div>
                                    <TourButton
                                        tourId="content-overview"
                                        className="h-[36px] w-[36px] p-0"
                                    >
                                        <IconHelpCircle size={16} />
                                    </TourButton>
                                </div>
                            </div>
                        </div>
                    )}
                    <div
                        className={`relative flex h-full flex-col-reverse overflow-auto ${admin && ""}`}
                    >
                        {!admin && (
                            <>
                                <link
                                    rel="manifest"
                                    href={
                                        domain
                                            ? "/manifest/account/"
                                            : "/" +
                                            user.username +
                                            "/manifest/account/"
                                    }
                                />
                                <link rel="icon" href={user.pic} sizes="any" />
                                <title>{post ? postTitle : title}</title>
                                <meta
                                    name="keywords"
                                    content={[
                                        ...(post?.tags
                                            ? post.tags.map((tag) =>
                                                tag.replaceAll("-", " ")
                                            )
                                            : []),
                                        ...(user?.tags
                                            ? user.tags.map((tag) =>
                                                tag.replaceAll("-", " ")
                                            )
                                            : []),
                                        "doras",
                                        "link in bio",
                                        "doras.to",
                                        "linkinbio",
                                        "mediakit",
                                        "streamer tools",
                                        "tools for content creators",
                                        "free link in bio",
                                        "free website builder",
                                    ].join(", ")}
                                />
                                <meta
                                    name="description"
                                    content={post ? post.title : user.bio}
                                />
                                <meta
                                    property="og:title"
                                    content={post ? postTitle : title}
                                />
                                <meta
                                    property="og:description"
                                    content={user.bio}
                                />
                                <meta
                                    property="og:image"
                                    content={
                                        post
                                            ? `/api/user/` +
                                            user.username +
                                            `/pic/blogpost/` +
                                            `?blog=` +
                                            post.title +
                                            `&blogPic=` +
                                            encodeURIComponent(post.img)
                                            : user.pic
                                    }
                                />
                                <meta
                                    property="og:url"
                                    content={`https://${process.env.NEXT_PUBLIC_API_SERVER_INTERNAL}/${user.username}`}
                                />
                                <meta property="og:type" content="website" />
                                {/* twitter */}
                                <meta
                                    property="twitter:card"
                                    content="summary_large_image"
                                />
                                <meta
                                    property="twitter:image"
                                    content={
                                        post
                                            ? `/api/user/` +
                                            user.username +
                                            `/pic/blogpost/` +
                                            `?blog=` +
                                            post.title +
                                            `&blogPic=` +
                                            encodeURIComponent(post.img)
                                            : user.pic
                                    }
                                />
                                <meta
                                    property="twitter:url"
                                    content={`https://${process.env.NEXT_PUBLIC_API_SERVER_INTERNAL}/${user.username}`}
                                />
                                <meta
                                    name="twitter:title"
                                    content={post ? postTitle : title}
                                />
                                <meta
                                    property="twitter:domain"
                                    content={
                                        process.env
                                            .NEXT_PUBLIC_API_SERVER_INTERNAL
                                    }
                                />
                            </>
                        )}
                        <div className="relative flex h-full max-h-full w-full">
                            <div className="onboarding-sidebar-selection relative h-full">
                                {admin && (
                                    <SidebarDrawer
                                        addBorder
                                        id={expandedContent?.id}
                                        title={expandedContent?.title || ""}
                                        description={
                                            expandedContent?.description
                                        }
                                        className={cn("bg-background", "")}
                                        options={
                                            <div className="flex items-center gap-3">
                                                {expandedContent?.titleOptions}
                                                {expandedContent?.titleOptions && (
                                                    <Separator
                                                        orientation="vertical"
                                                        className="h-6"
                                                    />
                                                )}
                                                <Button
                                                    onClick={() => [
                                                        setIsExpanded(false),
                                                        window.dispatchEvent(
                                                            new CustomEvent(
                                                                "closeBlock",
                                                                {
                                                                    detail: {
                                                                        id: "-1",
                                                                    },
                                                                }
                                                            )
                                                        ),
                                                    ]}
                                                    className="h-9 w-9 bg-card p-0 hover:bg-muted"
                                                >
                                                    <IconX className="cursor-pointer" />
                                                </Button>
                                            </div>
                                        }
                                        open={isMobile == true && isExpanded}
                                        onOpenChange={(value) => {
                                            if (isMobile == true) {
                                                [
                                                    setIsExpanded(value),
                                                    window.dispatchEvent(
                                                        new CustomEvent(
                                                            "closeBlock",
                                                            {
                                                                detail: {
                                                                    id: "-1",
                                                                },
                                                            }
                                                        )
                                                    ),
                                                ];
                                            }
                                        }}
                                    >
                                        {expandedContent?.content}
                                    </SidebarDrawer>
                                )}
                            </div>

                            <div
                                className={cn(
                                    "w-full",
                                    admin && "p-2",
                                    admin && isExpanded && "md:pl-0",
                                    admin && isMobile && "pb-0",
                                    admin && isMobile && isExpanded && "pb-56"
                                )}
                            >
                                <motion.div
                                    style={BackgroundStyle}
                                    id="user-root"
                                    initial={{ width: "100%" }}
                                    // onClick={handleClickOutside}
                                    transition={{
                                        duration: 0.1,
                                        ease: "easeInOut",
                                    }}
                                    className={`noscroll relative h-full overflow-auto ${className} ${admin ? "rounded-xl" : " min-h-dvh"}
                                    `}
                                >
                                    <div
                                        className={cn(
                                            "relative z-50 mx-auto flex min-h-full w-full flex-1 flex-col gap-4 px-3 pt-6 md:px-6",
                                            post ? " max-w-5xl" : "max-w-2xl"
                                        )}
                                        style={font_style}
                                    >
                                        {post ||
                                            (showLatestPost && latestPost) ? (
                                            <>
                                                <BlogPost
                                                    post={post || latestPost!}
                                                    user={user}
                                                    impersonate={impersonate}
                                                    theme={theme}
                                                    blogTheme={blogTheme}
                                                />
                                            </>
                                        ) : isLoading ? (
                                            <BlogPostSkeletonBlock
                                                account={user}
                                            />
                                        ) : (
                                            <>
                                                <ProfileShare
                                                    theme={theme}
                                                    user={user}
                                                    admin={false}
                                                    customTrigger={
                                                        <Button
                                                            id="share-button"
                                                            // variant="navigationAccent"
                                                            className={` sticky top-2 z-50 -mb-14 -mt-3 ml-auto mr-3 flex shrink-0 gap-2 ${theme.button_rounded} h-auto w-fit bg-primary p-2 text-foreground shadow-xl transition-all duration-300 hover:bg-muted hover:text-primary-foreground`}
                                                            style={{
                                                                color: textColor,
                                                                background:
                                                                    backgroundColor,
                                                            }}
                                                        >
                                                            <IconShare />
                                                        </Button>
                                                    }
                                                />
                                                <div
                                                    ref={ref}
                                                    className="flex"
                                                    id="onboarding-profile-heading"
                                                >
                                                    <HeadingDefault
                                                        user={user}
                                                        admin={editMode}
                                                        theme={theme}
                                                        setTheme={setTheme}
                                                        impersonate={
                                                            impersonate
                                                        }
                                                        ws={ws}
                                                        loggedInUser={
                                                            loggedInUser
                                                        }
                                                    />
                                                </div>

                                                <Blocks
                                                    user={user}
                                                    admin={editMode}
                                                    blocks={
                                                        !page
                                                            ? user.links
                                                            : page.blocks
                                                    }
                                                    assets={assets}
                                                    page={page}
                                                    theme={theme}
                                                    connections={connections}
                                                    impersonate={impersonate}
                                                    ws={ws}
                                                    loggedInUser={loggedInUser}
                                                    posts={posts || []}
                                                    blogTheme={blogTheme}
                                                    blackListUrls={
                                                        blackListUrls
                                                    }
                                                />
                                            </>
                                        )}
                                        {user.settings.branding == false && (
                                            <div
                                                id="doras-branding-root"
                                                className={`${admin && "pointer-events-none"} relative z-50 mx-auto flex  w-full max-w-2xl flex-1 flex-col gap-4 px-3 pt-6 md:px-6`}
                                            >
                                                {user.affiliate == null ? (
                                                    <a
                                                        href="https://doras.to"
                                                        className={`group mx-auto my-16 flex w-fit flex-col items-center gap-2 ${admin && "pointer-events-none"}`}
                                                    >
                                                        <Label
                                                            variant="headingtoggle"
                                                            className="mx-auto cursor-pointer text-center text-2xl font-bold"
                                                        >
                                                            Create Your
                                                        </Label>
                                                        <div className="mx-auto flex w-fit items-center gap-3 rounded-full bg-background px-6 py-4 text-foreground transition-all duration-200 group-hover:scale-105">
                                                            <img
                                                                src="https://cdn.doras.to/doras/icon-white.svg"
                                                                className="h-8 w-8"
                                                                alt="Doras Logo"
                                                            />
                                                            <Label
                                                                variant="headingtoggle"
                                                                className="cursor-pointer text-2xl font-bold"
                                                            >
                                                                Doras
                                                            </Label>
                                                        </div>
                                                    </a>
                                                ) : (
                                                    <a
                                                        id="affiliate-doras-branding-root"
                                                        href={`https://doras.to?aff=${user.affiliate}`}
                                                        className={`group relative mx-auto my-16 flex w-full flex-col items-center gap-2 overflow-hidden py-3 transition-all duration-300 hover:scale-110 ${user.theme.button_rounded} ${admin && "pointer-events-none"}`}
                                                        style={{
                                                            background:
                                                                user.theme
                                                                    .accent_col ||
                                                                user.theme
                                                                    .button,
                                                        }}
                                                    >
                                                        <img
                                                            id="affiliate-doras-branding-img"
                                                            src={user.pic}
                                                            className="absolute -bottom-5 -right-6 size-36 -rotate-12"
                                                            alt="Doras Logo"
                                                        />
                                                        <Label
                                                            id="affiliate-doras-branding-label"
                                                            variant="headingtoggle"
                                                            className="z-50 mx-auto cursor-pointer text-center text-xl font-bold"
                                                        >
                                                            Join{" "}
                                                            {user.displayname}{" "}
                                                            on
                                                        </Label>
                                                        <div className="z-50 mx-auto flex w-fit items-center gap-3 text-foreground transition-all duration-200 group-hover:scale-105">
                                                            <img
                                                                src="https://cdn.doras.to/doras/icon-white.svg"
                                                                className="h-8 w-8"
                                                                alt="Doras Logo"
                                                            />
                                                            <Label
                                                                variant="headingtoggle"
                                                                className="cursor-pointer text-2xl font-bold"
                                                                id="affiliate-doras-branding-label"
                                                            >
                                                                Doras
                                                            </Label>
                                                        </div>
                                                    </a>
                                                )}
                                            </div>
                                        )}

                                        <BottomNav
                                            user={user}
                                            admin={editMode}
                                            isHeadingInView={false}
                                            theme={theme}
                                            pages={newPages}
                                            domain={domain}
                                            impersonate={impersonate}
                                        />
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </SidebarInset>
            </SidebarProvider>
        </div>
    );
}
